import React, { useState } from 'react';

const ResultsDisplay = ({ results, loading, searchQuery }) => {
  const [copiedItem, setCopiedItem] = useState(null);
  
  // Debug: Log the results to see what we're getting
  console.log('Results received:', results);
  
  // Email validation function - Strict validation for proper email format
  const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    
    // Trim whitespace
    const trimmedEmail = email.trim();
    
    // Basic email regex - more strict
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    
    if (!emailRegex.test(trimmedEmail)) {
      return false;
    }
    
    // Additional checks
    const [localPart, domain] = trimmedEmail.split('@');
    
    // Local part should not start or end with dot
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      return false;
    }
    
    // Local part should not have consecutive dots
    if (localPart.includes('..')) {
      return false;
    }
    
    // Domain should not start or end with dot or hyphen
    if (domain.startsWith('.') || domain.endsWith('.') || 
        domain.startsWith('-') || domain.endsWith('-')) {
      return false;
    }
    
    // Domain should have at least one dot
    if (!domain.includes('.')) {
      return false;
    }
    
    return true;
  };
  
  // Phone validation function - Strict validation for Indian phone numbers
  const isValidPhone = (phone) => {
    if (!phone || typeof phone !== 'string') return false;
    
    // Remove all spaces, hyphens, and other separators
    const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
    
    // Check if it looks like a date (YYYYMMDD format)
    if (/^\d{8}$/.test(cleanPhone)) {
      const year = parseInt(cleanPhone.substring(0, 4));
      const month = parseInt(cleanPhone.substring(4, 6));
      const day = parseInt(cleanPhone.substring(6, 8));
      
      // If it's a reasonable date, it's probably not a phone number
      if (year >= 1900 && year <= 2030 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return false;
      }
    }
    
    // Check for valid Indian phone number patterns:
    // 1. 10 digits: 9876543210
    // 2. +91 followed by 10 digits: +919876543210
    // 3. 91 followed by 10 digits: 919876543210
    
    const patterns = [
      /^\d{10}$/,                    // 10 digits
      /^\+91\d{10}$/,               // +91 followed by 10 digits
      /^91\d{10}$/                  // 91 followed by 10 digits
    ];
    
    return patterns.some(pattern => pattern.test(cleanPhone));
  };
  
  const getColumnsFromQuery = (query) => {
    // Since backend only returns emails, we only show titles and emails
    return { titles: true, emails: true, phones: false, addresses: false };
  };
  
  const getCategoryFromQuery = (query) => {
    if (!query) return 'Results';
    
    const lowerQuery = query.toLowerCase();
    
    // Define category mappings
    const categories = {
      'school': 'Schools',
      'schools': 'Schools',
      'college': 'Colleges',
      'colleges': 'Colleges',
      'university': 'Universities',
      'universities': 'Universities',
      'agency': 'Agencies',
      'agencies': 'Agencies',
      'organization': 'Organizations',
      'organizations': 'Organizations',
      'orgs': 'Organizations',
      'company': 'Companies',
      'companies': 'Companies',
      'business': 'Businesses',
      'businesses': 'Businesses',
      'hospital': 'Hospitals',
      'hospitals': 'Hospitals',
      'clinic': 'Clinics',
      'clinics': 'Clinics',
      'restaurant': 'Restaurants',
      'restaurants': 'Restaurants',
      'hotel': 'Hotels',
      'hotels': 'Hotels',
      'shop': 'Shops',
      'shops': 'Shops',
      'store': 'Stores',
      'stores': 'Stores',
      'bank': 'Banks',
      'banks': 'Banks',
      'lawyer': 'Lawyers',
      'lawyers': 'Lawyers',
      'attorney': 'Attorneys',
      'attorneys': 'Attorneys',
      'doctor': 'Doctors',
      'doctors': 'Doctors',
      'dentist': 'Dentists',
      'dentists': 'Dentists'
    };
    
    // Find the first matching category
    for (const [keyword, category] of Object.entries(categories)) {
      if (lowerQuery.includes(keyword)) {
        return category;
      }
    }
    
    // If no specific category found, try to extract a general term
    const words = query.split(' ');
    const firstWord = words[0];
    if (firstWord && firstWord.length > 2) {
      return firstWord.charAt(0).toUpperCase() + firstWord.slice(1) + 's';
    }
    
    return 'Results';
  };
  
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(type);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const getAllTitles = () => {
    return results.map(result => result.title || result.url || 'Untitled Page').join('\n');
  };

  const getAllEmails = () => {
    // If results is a direct array of emails (strings)
    if (Array.isArray(results) && results.length > 0 && typeof results[0] === 'string') {
      return [...new Set(results)].join('\n'); // Remove duplicates
    }
    
    // If results is an array of objects with emails property
    const emails = [];
    results.forEach(result => {
      if (result.emails && result.emails.length > 0) {
        emails.push(...result.emails);
      }
    });
    return [...new Set(emails)].join('\n'); // Remove duplicates
  };

  const getAllPhones = () => {
    const phones = [];
    results.forEach(result => {
      if (result.phones && result.phones.length > 0) {
        phones.push(...result.phones);
      }
    });
    return [...new Set(phones)].join('\n'); // Remove duplicates
  };
  
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <div>Scraping websites for email addresses...</div>
      </div>
    );
  }

  if (!results || results.length === 0) {
    return (
      <div className="no-results">
        <div>No email addresses found. Try a different search term.</div>
      </div>
    );
  }

  return (
    <div className="results">
      <div className="results-header">
        <div className="results-title">
          {Array.isArray(results) && results.length > 0 && typeof results[0] === 'string' 
            ? `Email Addresses Found (${results.length})`
            : `${getCategoryFromQuery(searchQuery)} (${results.length})`
          }
        </div>
      </div>
      
      <div className="results-table-container">
        <table className="results-table">
          <thead>
            <tr>
              {getColumnsFromQuery(searchQuery).titles && (
                <th>
                  Website Title
                  <span 
                    className={`copy-icon ${copiedItem === 'titles' ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(getAllTitles(), 'titles')}
                    title="Copy all titles"
                  >
                    {copiedItem === 'titles' ? '✓' : '📋'}
                  </span>
                </th>
              )}
              {getColumnsFromQuery(searchQuery).emails && (
                <th>
                  Emails
                  <span 
                    className={`copy-icon ${copiedItem === 'emails' ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(getAllEmails(), 'emails')}
                    title="Copy all emails"
                  >
                    {copiedItem === 'emails' ? '✓' : '📋'}
                  </span>
                </th>
              )}
              {getColumnsFromQuery(searchQuery).phones && (
                <th>
                  Phone Numbers
                  <span 
                    className={`copy-icon ${copiedItem === 'phones' ? 'copied' : ''}`}
                    onClick={() => copyToClipboard(getAllPhones(), 'phones')}
                    title="Copy all phone numbers"
                  >
                    {copiedItem === 'phones' ? '✓' : '📋'}
                  </span>
                </th>
              )}
              {getColumnsFromQuery(searchQuery).addresses && (
                <th>Address</th>
              )}
            </tr>
          </thead>
          <tbody>
            {/* Check if results is a direct array of emails */}
            {Array.isArray(results) && results.length > 0 && typeof results[0] === 'string' ? (
              <tr>
                <td className="url-cell">
                  <div className="email-source">Email Addresses Found</div>
                </td>
                <td className="contact-cell">
                  {results.slice(0, 10).map((email, i) => (
                    <div key={i} className="contact-item email">
                      {isValidEmail(email) ? (
                        <a href={`mailto:${email}`}>{email}</a>
                      ) : (
                        <span className="invalid-contact" style={{color: '#ff6b6b', fontStyle: 'italic'}}>Invalid Email</span>
                      )}
                    </div>
                  ))}
                  {results.length > 10 && (
                    <div className="more-emails">... and {results.length - 10} more emails</div>
                  )}
                </td>
              </tr>
            ) : (
              /* Original object-based results */
              results.map((result, index) => (
                <tr key={index}>
                  {getColumnsFromQuery(searchQuery).titles && (
                    <td className="url-cell">
                      <a href={result.url} target="_blank" rel="noopener noreferrer" className="url-link" title={result.url}>
                        {result.title || result.url || 'Untitled Page'}
                      </a>
                    </td>
                  )}
                  
                  {getColumnsFromQuery(searchQuery).emails && (
                    <td className="contact-cell">
                      {result.emails && result.emails.length > 0 ? (
                        result.emails.slice(0, 3).map((email, i) => (
                          <div key={i} className="contact-item email">
                            {isValidEmail(email) ? (
                              <a href={`mailto:${email}`}>{email}</a>
                            ) : (
                              <span className="invalid-contact" style={{color: '#ff6b6b', fontStyle: 'italic'}}>Invalid Email</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="no-data-cell">—</div>
                      )}
                    </td>
                  )}
                  
                  {getColumnsFromQuery(searchQuery).phones && (
                    <td className="contact-cell">
                      {result.phones && result.phones.length > 0 ? (
                        result.phones.slice(0, 2).map((phone, i) => (
                          <div key={i} className="contact-item phone">
                            {isValidPhone(phone) ? (
                              <a href={`tel:${phone}`}>{phone}</a>
                            ) : (
                              <span className="invalid-contact" style={{color: '#ff6b6b', fontStyle: 'italic'}}>Invalid Phone</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="no-data-cell">—</div>
                      )}
                    </td>
                  )}
                  
                  {getColumnsFromQuery(searchQuery).addresses && (
                    <td className="address-cell">
                      {result.addresses && result.addresses.length > 0 ? (
                        result.addresses.map((address, i) => (
                          <div key={i} className="contact-item address">
                            {address}
                          </div>
                        ))
                      ) : result.error ? (
                        <div className="error-cell">
                          Error: {result.error}
                        </div>
                      ) : (
                        <div className="no-data-cell">—</div>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResultsDisplay;
